/**
 * SmartMessageService — wrapper around sendSmartMessage / respondToSmartMessage CFs
 * and Firestore subscriptions for the smartMessages collection.
 *
 * @module services/messaging/SmartMessageService
 */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { functions, db, storage } from '../../firebase';

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB

// ─── Cloud Function callers ─────────────────────────────────────────────────

/**
 * Send a new smart message via Cloud Function.
 *
 * @param {string} schoolId
 * @param {Object} payload
 * @param {string} payload.title
 * @param {string} payload.body
 * @param {string[]} payload.recipientStudentIds
 * @param {string} payload.responseFormat  — one of RESPONSE_FORMATS values
 * @param {string} [payload.attachmentUrl]
 * @param {string} [payload.attachmentName]
 * @param {import('firebase/firestore').Timestamp} [payload.expiresAt]
 * @returns {Promise<{smartMessageId: string, recipientCount: number}>}
 */
export const sendSmartMessage = (schoolId, payload) =>
  httpsCallable(functions, 'sendSmartMessage')({ schoolId, ...payload });

/**
 * Respond to a smart message via Cloud Function.
 *
 * @param {string} schoolId
 * @param {Object} payload
 * @param {string} payload.smartMessageId
 * @param {string} payload.response  — e.g. 'agree' | 'decline' | 'acknowledge'
 * @returns {Promise<void>}
 */
export const respondToSmartMessage = (schoolId, payload) =>
  httpsCallable(functions, 'respondToSmartMessage')({ schoolId, ...payload });

// ─── Firestore subscriptions ────────────────────────────────────────────────

/**
 * Subscribe to all smart messages for a school, newest-first.
 *
 * @param {string} schoolId
 * @param {Function} callback  — receives SmartMessage[] on each update
 * @returns {Function} unsubscribe
 */
export const listenToSmartMessages = (schoolId, callback) => {
  if (!schoolId) return () => {};

  const q = query(
    collection(db, 'schools', schoolId, 'smartMessages'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error('[SmartMessageService] listenToSmartMessages error:', err);
    }
  );
};

/**
 * Subscribe to a single smart message document + its recipients sub-collection.
 * Calls callback with { message, recipients } once both are loaded.
 *
 * @param {string} schoolId
 * @param {string} messageId
 * @param {Function} callback  — receives { message, recipients }
 * @returns {Function} unsubscribe
 */
export const listenToSmartMessageDetails = (schoolId, messageId, callback) => {
  if (!schoolId || !messageId) return () => {};

  let messageData = null;
  let recipientsData = null;

  const notify = () => {
    if (messageData !== null && recipientsData !== null) {
      callback({ message: messageData, recipients: recipientsData });
    }
  };

  const unsubMsg = onSnapshot(
    doc(db, 'schools', schoolId, 'smartMessages', messageId),
    (snap) => {
      messageData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      notify();
    },
    (err) => {
      console.error('[SmartMessageService] listenToSmartMessageDetails message error:', err);
    }
  );

  const unsubRecipients = onSnapshot(
    collection(db, 'schools', schoolId, 'smartMessages', messageId, 'recipients'),
    (snap) => {
      recipientsData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      notify();
    },
    (err) => {
      console.error('[SmartMessageService] listenToSmartMessageDetails recipients error:', err);
    }
  );

  return () => {
    unsubMsg();
    unsubRecipients();
  };
};

/**
 * Subscribe to a single parent's recipient doc for one smart message. The
 * recipient doc is the transactional source of truth — `status` flips to
 * "responded" inside the same transaction as the response, so this is the
 * most reliable signal for "did I respond?". Don't rely on the best-effort
 * copy on the message doc (`delivery.inApp.response`).
 *
 * @param {string} schoolId
 * @param {string} smartMessageId
 * @param {string} studentId
 * @param {Function} callback - receives recipient doc data or null
 * @returns {Function} unsubscribe
 */
export const listenToSmartMessageRecipient = (
  schoolId,
  smartMessageId,
  studentId,
  callback,
) => {
  if (!schoolId || !smartMessageId || !studentId) return () => {};
  return onSnapshot(
    doc(
      db,
      'schools',
      schoolId,
      'smartMessages',
      smartMessageId,
      'recipients',
      studentId,
    ),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (err) => {
      console.error('[SmartMessageService] listenToSmartMessageRecipient error:', err);
    },
  );
};

// ─── Attachment upload ──────────────────────────────────────────────────────

/**
 * Upload an attachment for a smart message to Firebase Storage.
 *
 * @param {string} schoolId
 * @param {File} file
 * @param {Function} [onProgress]  — receives 0-100
 * @returns {Promise<{url: string, name: string, storagePath: string}>}
 */
export const uploadSmartMessageAttachment = (schoolId, file, onProgress) => {
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return Promise.reject(new Error('File too large. Maximum size is 10 MB.'));
  }

  const tempId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  const storagePath = `schools/${schoolId}/smartMessages/${tempId}/${file.name}`;
  const fileRef = ref(storage, storagePath);

  if (onProgress) {
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(fileRef, file);
      task.on(
        'state_changed',
        (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => {
          try {
            const url = await getDownloadURL(fileRef);
            resolve({ url, name: file.name, storagePath });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  }

  return uploadBytes(fileRef, file).then(async () => {
    const url = await getDownloadURL(fileRef);
    return { url, name: file.name, storagePath };
  });
};

// ─── Class wrapper (used by SmartMessageComposer + tests) ────────────────────

/**
 * SmartMessageService class — thin wrapper around the sendSmartMessage CF.
 * Stateless; no React imports. Exported as both named and default.
 */
export class SmartMessageService {
  /**
   * @param {Object} payload — passed through unchanged to the CF.
   * @returns {Promise<{smartMessageId: string, recipientCount: number}>}
   */
  static async sendSmartMessage(payload) {
    const callable = httpsCallable(functions, 'sendSmartMessage');
    const result = await callable(payload);
    return result.data;
  }
}

export default SmartMessageService;
